"use client";

import * as React from "react";

// Types for notification system
export interface Notification {
  id: string;
  type: 'candidate_incomplete' | 'candidate_welcome_back' | 'hr_review_pending' | 'hr_urgent_review';
  recipientType: 'candidate' | 'hr';
  recipientId: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  scheduledFor?: string;
  sentAt?: string;
  readAt?: string;
  data?: Record<string, any>;
}

export interface CandidateProgress {
  candidateId: string;
  candidateName: string;
  email: string;
  onboardingStarted: string;
  lastActivity: string;
  currentStep: string;
  isComplete: boolean;
  submittedForReview?: string;
  hrReviewedAt?: string;
  joinerType?: string;
}

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  lastChecked: string;
}

// Notification Manager Service
class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private candidates: CandidateProgress[] = [];
  private checkInterval: NodeJS.Timeout | null = null;
  
  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  constructor() {
    // Initialize with demo data
    this.initializeDemoData();
    this.startPeriodicCheck();
  }

  private initializeDemoData() {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const twoDaysAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);

    // Demo candidates with various states
    this.candidates = [
      {
        candidateId: 'cand_001',
        candidateName: 'Sarah Johnson',
        email: 'sarah.johnson@email.com',
        onboardingStarted: yesterday.toISOString(),
        lastActivity: yesterday.toISOString(),
        currentStep: 'astro-review',
        isComplete: false,
        joinerType: 'employee'
      },
      {
        candidateId: 'cand_002',
        candidateName: 'Mike Chen',
        email: 'mike.chen@email.com',
        onboardingStarted: twoDaysAgo.toISOString(),
        lastActivity: new Date(now.getTime() - 30 * 60 * 1000).toISOString(),
        currentStep: 'astro-final',
        isComplete: true,
        submittedForReview: twoDaysAgo.toISOString(),
        joinerType: 'consultant'
      },
      {
        candidateId: 'cand_003',
        candidateName: 'Emma Wilson',
        email: 'emma.wilson@email.com',
        onboardingStarted: new Date(now.getTime() - 26 * 60 * 60 * 1000).toISOString(),
        lastActivity: new Date(now.getTime() - 25 * 60 * 60 * 1000).toISOString(),
        currentStep: 'astro-bulk',
        isComplete: false,
        joinerType: 'intern'
      }
    ];

    // Generate initial notifications
    this.checkAndCreateNotifications();
  }

  private startPeriodicCheck() {
    // Check for new notifications every 5 minutes (in demo, check every 10 seconds)
    this.checkInterval = setInterval(() => {
      this.checkAndCreateNotifications();
    }, 10000);
  }

  private checkAndCreateNotifications() {
    const now = new Date();

    // Check for candidate incomplete notifications (24+ hours)
    this.candidates.forEach(candidate => {
      if (!candidate.isComplete) {
        const lastActivity = new Date(candidate.lastActivity);
        const hoursSinceActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);

        if (hoursSinceActivity >= 24) {
          const existingNotification = this.notifications.find(
            n => n.type === 'candidate_incomplete' && 
                 n.recipientId === candidate.candidateId &&
                 !n.sentAt
          );

          if (!existingNotification) {
            this.createCandidateIncompleteNotification(candidate);
          }
        }
      }
    });

    // Check for HR review pending notifications (48+ hours)
    this.candidates.forEach(candidate => {
      if (candidate.isComplete && candidate.submittedForReview && !candidate.hrReviewedAt) {
        const submitted = new Date(candidate.submittedForReview);
        const hoursSinceSubmission = (now.getTime() - submitted.getTime()) / (1000 * 60 * 60);

        if (hoursSinceSubmission >= 48) {
          const existingNotification = this.notifications.find(
            n => n.type === 'hr_review_pending' && 
                 n.data?.candidateId === candidate.candidateId &&
                 !n.sentAt
          );

          if (!existingNotification) {
            this.createHRReviewPendingNotification(candidate);
          }
        }
      }
    });
  }

  private createCandidateIncompleteNotification(candidate: CandidateProgress) {
    const notification: Notification = {
      id: `notif_${Date.now()}_${candidate.candidateId}`,
      type: 'candidate_incomplete',
      recipientType: 'candidate',
      recipientId: candidate.candidateId,
      title: `Still with us, ${candidate.candidateName.split(' ')[0]}?`,
      message: "Astro noticed your mission is still in progress. Click below to resume onboarding — we're excited to have you!",
      priority: 'medium',
      createdAt: new Date().toISOString(),
      data: {
        candidateName: candidate.candidateName,
        currentStep: candidate.currentStep,
        email: candidate.email
      }
    };

    this.notifications.push(notification);
    this.sendEmailNotification(notification);
  }

  private createHRReviewPendingNotification(candidate: CandidateProgress) {
    const notification: Notification = {
      id: `notif_${Date.now()}_hr_${candidate.candidateId}`,
      type: 'hr_review_pending',
      recipientType: 'hr',
      recipientId: 'hr_team',
      title: "You've got a DayOne submission waiting",
      message: `${candidate.candidateName} submitted their onboarding documents and is waiting for review.`,
      priority: 'high',
      createdAt: new Date().toISOString(),
      data: {
        candidateId: candidate.candidateId,
        candidateName: candidate.candidateName,
        submittedAt: candidate.submittedForReview,
        joinerType: candidate.joinerType
      }
    };

    this.notifications.push(notification);
    this.sendEmailNotification(notification);
  }

  private async sendEmailNotification(notification: Notification) {
    // Simulate email sending
    setTimeout(() => {
      const index = this.notifications.findIndex(n => n.id === notification.id);
      if (index !== -1) {
        this.notifications[index].sentAt = new Date().toISOString();
      }
      console.log('Email sent:', notification);
    }, 1000);
  }

  // Public methods for components
  getNotificationsForUser(userId: string, userType: 'candidate' | 'hr'): Notification[] {
    return this.notifications.filter(
      n => n.recipientType === userType && 
           (userType === 'hr' || n.recipientId === userId)
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getUnreadCount(userId: string, userType: 'candidate' | 'hr'): number {
    return this.getNotificationsForUser(userId, userType).filter(n => !n.readAt).length;
  }

  markAsRead(notificationId: string) {
    const index = this.notifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      this.notifications[index].readAt = new Date().toISOString();
    }
  }

  markAllAsRead(userId: string, userType: 'candidate' | 'hr') {
    this.getNotificationsForUser(userId, userType).forEach(notification => {
      if (!notification.readAt) {
        this.markAsRead(notification.id);
      }
    });
  }

  createWelcomeBackNotification(candidateId: string, candidateName: string) {
    const notification: Notification = {
      id: `notif_${Date.now()}_welcome_${candidateId}`,
      type: 'candidate_welcome_back',
      recipientType: 'candidate',
      recipientId: candidateId,
      title: "Welcome back!",
      message: "Let's wrap things up, shall we?",
      priority: 'low',
      createdAt: new Date().toISOString(),
      data: {
        candidateName: candidateName
      }
    };

    this.notifications.push(notification);
    return notification;
  }

  // Get pending candidates for HR dashboard
  getPendingCandidates(): CandidateProgress[] {
    return this.candidates.filter(c => c.isComplete && c.submittedForReview && !c.hrReviewedAt);
  }

  // Update candidate status
  updateCandidateStatus(candidateId: string, updates: Partial<CandidateProgress>) {
    const index = this.candidates.findIndex(c => c.candidateId === candidateId);
    if (index !== -1) {
      this.candidates[index] = { ...this.candidates[index], ...updates };
    }
  }

  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
}

// React Hook for Notifications
export function useNotifications(userId: string, userType: 'candidate' | 'hr') {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const notificationService = React.useMemo(() => NotificationService.getInstance(), []);

  const refreshNotifications = React.useCallback(() => {
    const userNotifications = notificationService.getNotificationsForUser(userId, userType);
    const unread = notificationService.getUnreadCount(userId, userType);
    
    setNotifications(userNotifications);
    setUnreadCount(unread);
  }, [userId, userType, notificationService]);

  React.useEffect(() => {
    refreshNotifications();
    
    // Refresh notifications periodically
    const interval = setInterval(refreshNotifications, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, [refreshNotifications]);

  const markAsRead = React.useCallback((notificationId: string) => {
    notificationService.markAsRead(notificationId);
    refreshNotifications();
  }, [notificationService, refreshNotifications]);

  const markAllAsRead = React.useCallback(() => {
    notificationService.markAllAsRead(userId, userType);
    refreshNotifications();
  }, [userId, userType, notificationService, refreshNotifications]);

  const createWelcomeBack = React.useCallback((candidateName: string) => {
    return notificationService.createWelcomeBackNotification(userId, candidateName);
  }, [userId, notificationService]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    createWelcomeBack,
    refreshNotifications
  };
}

// Notification Context
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  createWelcomeBack: (candidateName: string) => Notification;
  refreshNotifications: () => void;
}

const NotificationContext = React.createContext<NotificationContextType | null>(null);

export function NotificationProvider({ 
  children, 
  userId, 
  userType 
}: { 
  children: React.ReactNode;
  userId: string;
  userType: 'candidate' | 'hr';
}) {
  const notificationHook = useNotifications(userId, userType);

  return (
    <NotificationContext.Provider value={notificationHook}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
}